import { isResource, resolveId } from '@medplum/core'
import type {
  CodeableConcept,
  Coverage,
  Organization,
  Patient,
  Reference,
  RelatedPerson,
} from '@medplum/fhirtypes'
import { match } from 'ts-pattern'
import {
  codeableConcept,
  codeFromCodeableConcept,
  displayFromCodeableConcept,
} from '#src/codeableConcept.ts'
import {
  COVERAGE_SYSTEM,
  ORGANIZATION_SYSTEM,
  SEEN_HEALTH_ORG_IDENTIFIER,
  subscriberRelationshipSelf,
} from '#src/codeSystems.ts'
import { ExtendedResource } from '#src/ExtendedResource.ts'
import { DocumentTypes } from '#src/models/document-references/constants.ts'
import type { QueryTypes } from '#src/search.ts'
import { isOfType, recordValues } from '#src/utils.ts'

export const CoverageType = {
  MEDICAID: 'medicaid',
  MEDICARE: 'medicare',
  SEEN_HEALTH: 'seen-health',
} as const
export type CoverageType = (typeof CoverageType)[keyof typeof CoverageType]

interface InsuranceIdValidation {
  valid: boolean
  normalized: string
  error?: string
}

export class ExtendedCoverage extends ExtendedResource<Coverage> {
  static readonly RESOURCE_TYPE = 'Coverage'
  static readonly TYPES = {
    [CoverageType.MEDICARE]: codeableConcept({
      system: COVERAGE_SYSTEM,
      code: CoverageType.MEDICARE,
      display: 'Medicare',
    }),
    [CoverageType.MEDICAID]: codeableConcept({
      system: COVERAGE_SYSTEM,
      code: CoverageType.MEDICAID,
      display: 'Medicaid',
    }),
    [CoverageType.SEEN_HEALTH]: codeableConcept({
      system: COVERAGE_SYSTEM,
      code: CoverageType.SEEN_HEALTH,
      display: 'Seen Health',
    }),
  } satisfies Record<string, CodeableConcept>

  static readonly TYPE_OPTIONS = recordValues(this.TYPES).map((value) => ({
    value: codeFromCodeableConcept(value) ?? '',
    label: displayFromCodeableConcept(value) ?? '',
  }))

  static readonly EXPECTED_MEDICAID_LENGTH = 9
  static readonly EXPECTED_MEDICARE_LENGTH = 11

  static normalizeMedicaidId(id: string): string {
    return id.trim().slice(0, ExtendedCoverage.EXPECTED_MEDICAID_LENGTH)
  }

  static normalizeMedicareId(id: string): string {
    return id
      .trim()
      .replace(/[^A-Z0-9]/gi, '')
      .toUpperCase()
  }

  static isValidMedicaidId(id: string): boolean {
    return (
      id.length === ExtendedCoverage.EXPECTED_MEDICAID_LENGTH &&
      /^\d{8}[A-Z]$/i.test(id)
    )
  }

  static isValidMedicareId(id: string): boolean {
    return (
      id.length === ExtendedCoverage.EXPECTED_MEDICARE_LENGTH &&
      /^[A-Z0-9]{11}$/i.test(id)
    )
  }

  static validateInsuranceId(
    id: string,
    type: CoverageType
  ): InsuranceIdValidation {
    if (type === CoverageType.SEEN_HEALTH) {
      return { valid: true, normalized: id }
    }

    const normalized =
      type === CoverageType.MEDICARE
        ? ExtendedCoverage.normalizeMedicareId(id)
        : ExtendedCoverage.normalizeMedicaidId(id)

    if (!normalized) {
      return {
        valid: false,
        normalized: '',
        error: 'ID is empty after normalization',
      }
    }

    const isValid =
      type === CoverageType.MEDICARE
        ? ExtendedCoverage.isValidMedicareId(normalized)
        : ExtendedCoverage.isValidMedicaidId(normalized)

    if (!isValid) {
      const expected =
        type === CoverageType.MEDICARE
          ? `${ExtendedCoverage.EXPECTED_MEDICARE_LENGTH} alphanumeric characters`
          : `${ExtendedCoverage.EXPECTED_MEDICAID_LENGTH} characters (8 digits + 1 letter)`
      return {
        valid: false,
        normalized,
        error: `Invalid format (expected ${expected})`,
      }
    }

    return { valid: true, normalized }
  }

  static getSubscriberIdFieldInfo(
    type: CoverageType
  ): { label: string; placeholder: string } | undefined {
    if (type === CoverageType.MEDICARE) {
      return {
        label: 'Medicare ID (MBI)',
        placeholder: '11 alphanumeric characters',
      }
    }
    if (type === CoverageType.MEDICAID) {
      return {
        label: 'Medi-Cal CIN',
        placeholder: '8 digits + 1 letter',
      }
    }
    return undefined
  }

  constructor({
    type,
    participant,
    startDate,
    endDate,
    subscriberId,
  }: {
    type: keyof typeof ExtendedCoverage.TYPES
    participant: Reference<Patient>
    startDate: string
    endDate?: string
    subscriberId?: string
  }) {
    super({
      type: ExtendedCoverage.TYPES[type],
      status: 'active',
      beneficiary: participant,
      subscriber: participant,
      relationship: subscriberRelationshipSelf,
      payor: [
        {
          reference: `Organization?identifier=${ORGANIZATION_SYSTEM}|${SEEN_HEALTH_ORG_IDENTIFIER}`,
        },
      ],
      subscriberId,
    })
    this.startDate = startDate
    this.endDate = endDate
  }

  get type(): CoverageType | undefined {
    return codeFromCodeableConcept(this.resource.type) as
      | CoverageType
      | undefined
  }

  set type(value: CoverageType) {
    this.resource.type = ExtendedCoverage.TYPES[value]
  }

  get typeDisplay(): string | undefined {
    return displayFromCodeableConcept(this.resource.type)
  }

  get documentType(): DocumentTypes | undefined {
    return match(this.type)
      .with(CoverageType.MEDICAID, () => DocumentTypes.MEDICAID_ID)
      .with(CoverageType.MEDICARE, () => DocumentTypes.MEDICARE_ID)
      .with(CoverageType.SEEN_HEALTH, () => undefined)
      .otherwise(() => undefined)
  }

  get beneficiary(): Reference<Patient> {
    return this.resource.beneficiary
  }

  set beneficiary(value: Reference<Patient>) {
    this.resource.beneficiary = value
  }

  get subscriber(): Coverage['subscriber'] {
    return this.resource.subscriber
  }

  set subscriber(value: Coverage['subscriber']) {
    this.resource.subscriber = value
  }

  get startDate(): string | undefined {
    return this.resource.period?.start
  }

  set startDate(value: string | undefined) {
    this.resource.period ??= {}
    this.resource.period.start = value
  }

  get endDate(): string | undefined {
    return this.resource.period?.end
  }

  set endDate(value: string | undefined) {
    this.resource.period ??= {}
    this.resource.period.end = value
  }

  get status(): Coverage['status'] {
    return this.resource.status
  }

  set status(value: Coverage['status']) {
    this.resource.status = value
  }

  get payor(): Reference<Organization | Patient | RelatedPerson>[] {
    return this.resource.payor
  }

  set payor(value: Reference<Organization | Patient | RelatedPerson>[]) {
    this.resource.payor = value
  }

  get subscriberId(): string | undefined {
    return this.resource.subscriberId
  }

  set subscriberId(value: string | undefined) {
    this.resource.subscriberId = value
  }

  get relationship(): CodeableConcept | undefined {
    return this.resource.relationship
  }

  set relationship(value: CodeableConcept | undefined) {
    this.resource.relationship = value
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(): QueryTypes {
    return {
      _sort: '-_lastUpdated',
      _count: '1000',
    }
  }

  static queryByType({
    type,
    ...query
  }: { type: CoverageType } & QueryTypes): QueryTypes {
    return {
      type: `${COVERAGE_SYSTEM}|${type}`,
      'status:not': 'entered-in-error',
      _sort: '-_lastUpdated',
      ...query,
    }
  }

  static byParticipant({
    participant,
  }: {
    participant: Reference<Patient>
  }): QueryTypes {
    return { beneficiary: participant, 'status:not': 'entered-in-error' }
  }

  static byParticipantAndType({
    participant,
    type,
  }: {
    participant: Reference<Patient>
    type: CoverageType
  }): QueryTypes {
    return {
      beneficiary: participant,
      status: 'active',
      type: `${COVERAGE_SYSTEM}|${type}`,
      _sort: '-_lastUpdated',
    }
  }

  static byParticipantAndTypeAllStatuses({
    participant,
    type,
  }: {
    participant: Reference<Patient>
    type: CoverageType
  }): QueryTypes {
    return {
      beneficiary: participant,
      ...ExtendedCoverage.queryByType({ type }),
    }
  }

  static isInstance(value: unknown): value is Coverage {
    return isResource(value) && isOfType(value, 'Coverage')
  }

  protected getExportFields() {
    return {
      id: this.id,
      type: this.type,
      display: this.typeDisplay,
      status: this.status,
      beneficiaryId: resolveId(this.beneficiary),
      subscriber: this.subscriber?.reference,
      startDate: this.startDate,
      endDate: this.endDate,
      subscriberId: this.subscriberId,
    }
  }
}
